import * as T from "three";
import { LiveParticles } from "./live-particles.js";
import { LiveScene } from "./live-scene.js";
import { DebugRendering } from "./debug-rendering.js";
import { installPaintMaterial } from './item-paint.js';
import { ViewerTeleports } from './viewer-teleports.js';
import { attachControls } from "./controls.js";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { Visibility } from "./visibility.js";
import { numbers } from "./bsp.js";
import { texturePath } from "./binary.js";
import { logger } from "./log.js";
import { skyFaces, skyGeometry } from "./skybox.js";
import { WaterSystem, isWater, waterSettings } from "./water.js";
import { LightingSystem, lightingMaterial, lightDirection } from "./lighting.js";
import { applyRenderState } from "./render-state.js";
import { WorldBatches } from "./world-batches.js";
const log = logger("Renderer");
const white = new T.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
white.needsUpdate = true;
export class MapRenderer {
    constructor(canvas, diagnostic, { controls = attachControls, previewClock = null, alpha = false } = {}) {
        this.diagnostic = diagnostic;
        this.canvas = canvas;
        this.onContextRestored = () => { this.staticFrame = null; };
        canvas.addEventListener("webglcontextrestored", this.onContextRestored);
        this.renderer = new T.WebGLRenderer({ canvas, antialias: true, alpha });
        this.renderer.outputColorSpace = T.SRGBColorSpace;
        // Count a complete frame, including water, sky and composite passes.
        this.renderer.info.autoReset = false;
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = T.PCFShadowMap;
        this.renderer.shadowMap.autoUpdate = false;
        // Three filters shadow casters against the viewing camera's layers.
        // Include off-PVS casters only during its shadow pass, then restore the
        // mask before Three draws the color pass.
        const renderShadows = this.renderer.shadowMap.render.bind(this.renderer.shadowMap);
        this.renderer.shadowMap.render = (lights, scene, camera) => {
            const mask = camera.layers.mask; camera.layers.enable(1);
            try { return renderShadows(lights, scene, camera); }
            finally { camera.layers.mask = mask; }
        };
        this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
        this.camera = new T.PerspectiveCamera(75, 1, 1, 100000);
        this.camera.up.set(0, 0, 1);
        this.camera.rotation.order = "ZYX";
        this.skyScene = new T.Scene();
        this.skyCamera = new T.PerspectiveCamera();
        this.scene = new T.Scene();
        this.scene.background = new T.Color(0x171d24);
        this.resources = new Set();
        this.water = new WaterSystem();
        this.lighting = new LightingSystem();
        this.meshes = [];
        this.propMeshes = [];
        this.brushPrototypes = [];
        this.assetTasks = [];
        this.materials = new Map();
        this.textures = new Map();
        this.animated = [];
        this.s3tc = this.renderer.extensions.has(
            "WEBGL_compressed_texture_s3tc",
        );
        log.info("WebGL initialized", { s3tc: this.s3tc,
            maxTextureSize: this.renderer.capabilities.maxTextureSize,
            pixelRatio: this.renderer.getPixelRatio() });
        this.epoch = 0;
        this.loading = false;
        this.keys = new Set();
        this.speed = 400;
        this.yaw = 0;
        this.pitch = 0;
        this.controls = controls(canvas, this, diagnostic);
        this.previewClock = previewClock;
        this.frame = this.frame.bind(this);
        this.previous = performance.now();
        this.frame();
    }
    track(resource) {
        this.staticFrame = null;
        this.resources.add(resource);
        return resource;
    }
    startLive(rpc, options) {
        this.mapParticles?.dispose(); this.mapParticles = null;
        this.liveScene?.dispose();
        return this.liveScene = new LiveScene(this, rpc, options);
    }
    clear() {
        this.staticFrame = null;
        this.worldBatches?.dispose(); this.worldBatches = null;
        this.performanceOverlay?.reset();
        this.debugRendering?.dispose(); this.debugRendering = null;
        this.liveScene?.dispose(); this.liveScene = null;
        this.mapParticles?.dispose(); this.mapParticles = null;
        log.info("clear scene", { epoch: this.epoch, resources: this.resources.size, meshes: this.meshes.length });
        for (const task of this.assetTasks ?? []) task.resolve();
        this.epoch++;
        this.water.clear();
        this.lighting.clear();
        this.loading = false;
        this.controls.release();
        for (const r of this.resources) r.dispose();
        this.resources.clear();
        this.scene.clear();
        this.skyScene.clear();
        this.skyDefinition = null;
        this.skyArea = -1;
        this.skyFaces = null;
        this.scene.background = new T.Color(0x171d24);
        this.scene.fog = this.skyScene.fog = null;
        this.meshes = [];
        this.propMeshes = [];
        this.brushPrototypes = [];
        this.assetTasks = [];
        this.materials.clear();
        this.textures.clear();
        this.animated = [];
        this.visibility = null; this.teleports = null;
        this.sky = null;
    }
    dispose() {
        cancelAnimationFrame(this.animation);
        this.performanceOverlay?.dispose();
        this.clear(); this.controls.dispose(); this.renderer.dispose();
        this.canvas.removeEventListener("webglcontextrestored", this.onContextRestored);
    }
    reset(entities, models) {
        log.debug("reset camera", { entities: entities.length, models: models.length });
        const spawns = entities.filter(
            (e) =>
                e.classname === "info_player_teamspawn" ||
                e.classname === "info_player_start",
        );
        const spawn = spawns[Math.floor(Math.random() * spawns.length)];
        const position = spawn
            ? numbers(spawn.origin)
            : models[0]
              ? models[0].mins.map((v, i) => (v + models[0].maxs[i]) / 2)
              : [0, 0, 100];
        this.camera.position.fromArray(position);
        this.camera.position.z += spawn ? 64 : 0;
        const angles = numbers(spawn?.angles);
        this.yaw = (angles[1] * Math.PI) / 180;
        this.pitch = (-angles[0] * Math.PI) / 180;
    }
    geometry(data) {
        const g = this.track(new T.BufferGeometry());
        g.setAttribute("position", new T.BufferAttribute(data.positions, 3));
        g.setAttribute("uv", new T.BufferAttribute(data.uv, 2));
        if (data.uv2) {
            g.setAttribute("uv1", new T.BufferAttribute(data.uv2, 2));
            g.setAttribute("blend", new T.BufferAttribute(data.alpha, 1));
        }
        g.setIndex(new T.BufferAttribute(data.indices, 1));
        if (data.normals)
            g.setAttribute("normal", new T.BufferAttribute(data.normals, 3));
        else g.computeVertexNormals();
        g.computeBoundingSphere();
        return g;
    }
    async texture(path, rpc, { linear = false, animation = false } = {}) {
        const key = `${path}:${linear}:${animation}`;
        if (!this.textures.has(key))
            this.textures.set(
                key,
                (async () => {
                    const epoch = this.epoch;
                    log.debug("load texture", { path, epoch });
                    const { texture: source } = await rpc("texture", {
                        path,
                        s3tc: this.s3tc,
                        animation,
                    });
                    if (epoch !== this.epoch)
                        throw new DOMException("Cancelled", "AbortError");
                    const frames = (source.frameFaces ?? [source.faces]).map(faces => {
                        let mipmaps = faces[0];
                        while (
                            mipmaps.length > 1 &&
                            (mipmaps[0].width >
                                this.renderer.capabilities.maxTextureSize ||
                                mipmaps[0].height >
                                    this.renderer.capabilities.maxTextureSize)
                        )
                            mipmaps = mipmaps.slice(1);
                        const first = mipmaps[0];
                        let texture;
                        if (source.compressed)
                            texture = new T.CompressedTexture(
                                mipmaps,
                                first.width,
                                first.height,
                                source.format === 13 || source.format === 20
                                    ? T.RGBA_S3TC_DXT1_Format
                                    : source.format === 14
                                      ? T.RGBA_S3TC_DXT3_Format
                                      : T.RGBA_S3TC_DXT5_Format,
                            );
                        else {
                            texture = new T.DataTexture(
                                first.data,
                                first.width,
                                first.height,
                                T.RGBAFormat,
                                first.data instanceof Float32Array
                                    ? T.FloatType
                                    : T.UnsignedByteType,
                            );
                            texture.mipmaps = mipmaps;
                        }
                        texture.wrapS =
                            source.flags & 4
                                ? T.ClampToEdgeWrapping
                                : T.RepeatWrapping;
                        texture.wrapT =
                            source.flags & 8
                                ? T.ClampToEdgeWrapping
                                : T.RepeatWrapping;
                        texture.magFilter = T.LinearFilter;
                        texture.minFilter =
                            mipmaps.length > 1
                                ? T.LinearMipmapLinearFilter
                                : T.LinearFilter;
                        texture.generateMipmaps = false;
                        texture.colorSpace = linear || first.data instanceof Float32Array
                            ? T.NoColorSpace : T.SRGBColorSpace;
                        texture.needsUpdate = true;
                        if (source.sheet) texture.userData.particleSheet = source.sheet;
                        log.debug("texture ready", { path, width: first.width, height: first.height,
                            mipmaps: mipmaps.length, compressed: source.compressed, format: source.format });
                        return this.track(texture);
                    });
                    // Still textures must remain cloneable for VMT transforms.
                    // Texture.copy serializes userData, so a frame list pointing
                    // back to the texture would make that operation circular.
                    if (animation) frames[0].userData.animationFrames = frames;
                    return frames[0];
                })(),
            );
        return this.textures.get(key);
    }
    async material(path, rpc, lightmap = null) {
        const key = `${path}:${lightmap?.uuid ?? ""}`;
        if (!this.materials.has(key))
            this.materials.set(
                key,
                (async () => {
                    const epoch = this.epoch;
                    log.debug("load material", { path, lightmap: !!lightmap, epoch });
                    const {
                        material: { shader, values: v },
                    } = await rpc("material", { path });
                    if (epoch !== this.epoch) throw new DOMException("Cancelled", "AbortError");
                    if (isWater(shader, v)) {
                        const settings = waterSettings(shader, v);
                        const load = async (path, options) => path ? this.texture(texturePath(path), rpc, options) : null;
                        const [normal, base, env, overlay, normal2, normalMask] = await Promise.all([
                            load(settings.normalPath, { linear: true, animation: true }),
                            load(settings.values.$basetexture, { animation: true }),
                            this.waterEnvironment(settings.values.$envmap, rpc),
                            this.waterOverlay(settings.values.$underwateroverlay, rpc),
                            load(settings.normal2Path, { linear: true, animation: true }),
                            load(settings.normalMaskPath, { linear: true }),
                        ]);
                        if (epoch !== this.epoch) throw new DOMException("Cancelled", "AbortError");
                        settings.overlay = overlay;
                        log.info("water material ready", { path, shader, above: settings.above, legacy: settings.legacy,
                            normalFrames: normal?.userData.animationFrames?.length ?? 0,
                            proxies: settings.proxies.map(p => p.type), forceCheap: settings.forceCheap,
                            dualNormals: !!normal2, normalMask: !!normalMask,
                            fogStart: settings.fogStart, fogEnd: settings.fogEnd });
                        return this.track(this.water.material(settings, normal, base, lightmap, env, normal2, normalMask));
                    }
                    if (
                        ![
                            "lightmappedgeneric",
                            "unlitgeneric",
                            "vertexlitgeneric",
                            "worldvertextransition",
                            "water",
                            "sky",
                        ].includes(shader)
                    )
                        this.diagnostic(
                            `Approximate shader: ${shader} (${path})`,
                        );
                    const map = v.$basetexture
                        ? await this.texture(texturePath(v.$basetexture), rpc)
                        : white;
                    if (epoch !== this.epoch)
                        throw new DOMException("Cancelled", "AbortError");
                    const transparent =
                        v.$translucent === "1" ||
                        v.$additive === "1" ||
                        shader === "water";
                    const color = new T.Color();
                    const tint = String(v.$color ?? v.$color2 ?? "[1 1 1]")
                        .match(/[-\d.]+/g)
                        ?.map(Number);
                    if (tint?.length >= 3)
                        color.setRGB(
                            ...tint
                                .slice(0, 3)
                                .map((x) =>
                                    String(v.$color ?? v.$color2).startsWith(
                                        "{",
                                    )
                                        ? x / 255
                                        : x,
                                ),
                        );
                    const options = {
                        map,
                        color,
                        transparent,
                        opacity: Math.min(
                            1,
                            Math.max(
                                0,
                                Number(
                                    v.$alpha ?? (shader === "water" ? 0.65 : 1),
                                ),
                            ),
                        ),
                        alphaTest:
                            v.$alphatest === "1"
                                ? Number(v.$alphatestreference ?? 0.5)
                                : 0,
                        side:
                            v.$nocull === "1" || shader === "water"
                                ? T.DoubleSide
                                : T.FrontSide,
                        depthWrite: !transparent,
                        shadowSide: T.FrontSide,
                    };
                    if (v.$additive === "1")
                        options.blending = T.AdditiveBlending;
                    let material;
                    const unlit = ["unlitgeneric", "unlittwotexture", "sky"].includes(shader);
                    if (!unlit)
                        material = new T.MeshLambertMaterial({ ...options, lightMap: lightmap, lightMapIntensity: Math.PI });
                    else
                        material = new T.MeshBasicMaterial({
                            ...options,
                            lightMap:
                                shader === "unlitgeneric" ? null : lightmap,
                            // BSP RGBExp samples already encode the surface
                            // multiplier. MeshBasicMaterial divides by pi for
                            // irradiance, so compensate for that convention.
                            lightMapIntensity: Math.PI,
                        });
                    if (shader === "worldvertextransition" && v.$basetexture2) {
                        const second = await this.texture(
                            texturePath(v.$basetexture2),
                            rpc,
                        );
                        material.onBeforeCompile = (program) => {
                            program.uniforms.map2 = { value: second };
                            program.vertexShader =
                                "attribute float blend; varying float vBlend;\n" +
                                program.vertexShader.replace(
                                    "#include <begin_vertex>",
                                    "#include <begin_vertex>\nvBlend = blend;",
                                );
                            program.fragmentShader =
                                "uniform sampler2D map2; varying float vBlend;\n" +
                                program.fragmentShader.replace(
                                    "#include <map_fragment>",
                                    "#ifdef USE_MAP\nvec4 a = texture2D(map, vMapUv); vec4 b = texture2D(map2, vMapUv); diffuseColor *= mix(a, b, clamp(vBlend, 0.0, 1.0));\n#endif",
                                );
                        };
                        material.customProgramCacheKey = () =>
                            "worldvertextransition";
                    }
                    if (v.$basetexturetransform) {
                        const transform =
                            /center\s+([\d.-]+)\s+([\d.-]+)\s+scale\s+([\d.-]+)\s+([\d.-]+)\s+rotate\s+([\d.-]+)\s+translate\s+([\d.-]+)\s+([\d.-]+)/i.exec(
                                v.$basetexturetransform,
                            );
                        if (transform) {
                            material.map = this.track(map.clone());
                            const n = transform.slice(1).map(Number);
                            material.map.center.set(n[0], n[1]);
                            material.map.repeat.set(n[2], n[3]);
                            material.map.rotation = (n[4] * Math.PI) / 180;
                            material.map.offset.set(n[5], n[6]);
                        }
                    }
                    const selfMask = v.$selfillum === "1" && v.$selfillummask
                        ? await this.texture(texturePath(v.$selfillummask), rpc, { linear: true }) : null;
                    const selfTint = new T.Color();
                    const selfColor = String(v.$selfillumtint ?? "1 1 1").match(/[-\d.]+/g)?.map(Number);
                    if (selfColor?.length >= 3) selfTint.setRGB(...selfColor.slice(0, 3));
                    material.userData.source = { unlit, selfIllum: v.$selfillum === "1" };
                    material.sourceEmission = { selfIllum: v.$selfillum === "1", mask: selfMask, tint: selfTint };
                    installPaintMaterial(material, v);
                    lightingMaterial(material, this.lighting, { ...material.sourceEmission });
                    if (epoch !== this.epoch) {
                        material.dispose();
                        throw new DOMException("Cancelled", "AbortError");
                    }
                    log.debug("material ready", { path, shader, transparent, side: options.side });
                    return this.track(material);
                })(),
            );
        return this.materials.get(key);
    }
    async waterEnvironment(path, rpc) {
        if (!path || path === "env_cubemap") return null;
        const key = `cube:${path}`, epoch = this.epoch;
        if (!this.textures.has(key)) this.textures.set(key, (async () => {
            try {
                const { texture: source } = await rpc("texture", { path: texturePath(path), s3tc: false });
                if (epoch !== this.epoch) throw new DOMException("Cancelled", "AbortError");
                if (source.faces.length !== 6) throw new Error("Water environment is not a cubemap");
                const images = source.faces.map(face => {
                    const mip = face[0];
                    return this.track(new T.DataTexture(mip.data, mip.width, mip.height, T.RGBAFormat,
                        mip.data instanceof Float32Array ? T.FloatType : T.UnsignedByteType));
                });
                const texture = this.track(new T.CubeTexture(images));
                texture.type = images[0].type;
                texture.colorSpace = texture.type === T.FloatType ? T.LinearSRGBColorSpace : T.SRGBColorSpace;
                texture.minFilter = texture.magFilter = T.LinearFilter; texture.generateMipmaps = false;
                texture.needsUpdate = true;
                log.debug("water cubemap ready", { path, faces: 6, width: source.width });
                return texture;
            } catch (error) {
                if (error.name === "AbortError") throw error;
                log.warn("water cubemap unavailable; using planar reflection", { path, message: error.message });
                return null;
            }
        })());
        return this.textures.get(key);
    }
    async waterOverlay(path, rpc) {
        if (!path) return null;
        const key = `overlay:${path}`;
        if (!this.textures.has(key)) this.textures.set(key, (async () => {
            const { material } = await rpc("material", { path });
            const settings = waterSettings("water_dudv", material.values);
            const normal = await this.texture(texturePath(settings.normalPath), rpc, { linear: true, animation: true });
            log.debug("underwater overlay ready", { path, shader: material.shader,
                frames: normal.userData.animationFrames.length });
            return { settings, normal };
        })());
        return this.textures.get(key);
    }
    captureWaterScene(camera, target, planes) {
        this.renderer.setRenderTarget(target);
        this.renderer.clippingPlanes = [];
        this.renderer.clear();
        const skyCamera = (this.waterSkyCamera ??= new T.PerspectiveCamera()).copy(camera);
        if (this.skyDefinition) skyCamera.position.copy(camera.position)
            .divideScalar(this.skyDefinition.scale).add(this.skyDefinition.origin);
        const skyPosition = this.sky?.position.clone();
        if (this.sky) { this.sky.position.copy(skyCamera.position); this.sky.updateMatrixWorld(); }
        try {
            this.renderer.render(this.skyScene, skyCamera);
            this.renderer.clearDepth();
            this.renderer.clippingPlanes = planes;
            this.renderer.render(this.scene, camera);
        } finally { if (this.sky) { this.sky.position.copy(skyPosition); this.sky.updateMatrixWorld(); } }
    }
    viewVisibility() {
        const clusters = this.debugOptions?.visibility ? null : this.visibility?.visibleClusters?.(this.camera.position.toArray());
        this.camera.updateMatrixWorld();
        const frustum = (this.viewFrustum ??= new T.Frustum()).setFromProjectionMatrix((this.viewMatrix ??= new T.Matrix4()).multiplyMatrices(
            this.camera.projectionMatrix, this.camera.matrixWorldInverse));
        return { clusters, frustum };
    }
    inPvs(clusters, visible) {
        return !visible || !clusters?.length || clusters.some(cluster => visible.has(cluster));
    }
    // World tasks are unconditional; prop tasks have PVS/frustum eligibility.
    // Tasks own their cancellation predicate so old loads cannot repump a new map.
    get assetsLoading() {
        return Boolean(this.liveScene?.running || this.assetTasks?.some(task => task.running));
    }
    assetTask(load, active, eligible = () => true) {
        const task = { load, active, eligible, started: false };
        task.promise = new Promise(resolve => task.resolve = resolve);
        task.ensure = () => { task.requested = true; this.pumpAssets(); return task.promise; };
        (this.assetTasks ??= []).push(task);
        return task;
    }
    pumpAssets(visibility = this.camera ? this.viewVisibility() : {}) {
        const tasks = this.assetTasks ?? [];
        for (let i = tasks.length - 1; i >= 0; i--) if (!tasks[i].started && !tasks[i].active()) {
            tasks[i].started = true; tasks[i].resolve(); tasks.splice(i, 1);
        }
        const running = tasks.filter(task => task.running).length;
        let slots = 6 - running;
        for (const task of tasks) {
            if (!slots) break;
            if (task.started || !task.active() || !task.requested && !task.eligible(visibility)) continue;
            task.started = task.running = true; slots--;
            void Promise.resolve(task.load()).catch(error => {
                if (task.active() && error.name !== "AbortError") this.diagnostic(error.message);
            }).finally(() => { task.running = false; task.resolve();
                if (this.assetTasks === tasks) {
                    tasks.splice(tasks.indexOf(task), 1);
                    task.load = task.active = task.eligible = null;
                    this.pumpAssets();
                } });
        }
    }
    async loadMapAssets() {
        const tasks = (this.assetTasks ?? []).filter(task => task.active());
        this.pumpAssets();
        await Promise.all(tasks.map(task => task.promise));
    }
    async loadVisibleAssets() {
        // Model preparation adds deferred instance tasks. Drain eligible work
        // through both stages without awaiting offscreen props.
        while (true) {
            const visibility = this.camera ? this.viewVisibility() : {};
            const tasks = (this.assetTasks ?? []).filter(task => task.active() &&
                (task.running || task.requested || task.eligible(visibility)));
            if (!tasks.length) return;
            for (const task of tasks) task.requested = true;
            this.pumpAssets(visibility);
            await Promise.all(tasks.map(task => task.promise));
        }
    }
    propGroupEligible(group, visibility) {
        let { clusters, frustum } = visibility;
        if (group[0].inSky && this.camera && this.skyDefinition) {
            if (!visibility.sky) {
                const camera = new T.PerspectiveCamera().copy(this.camera);
                camera.position.divideScalar(this.skyDefinition.scale).add(this.skyDefinition.origin);
                camera.updateMatrixWorld();
                visibility.sky = {
                    clusters: this.debugOptions?.visibility ? null : this.visibility.visibleClusters(camera.position.toArray()),
                    frustum: new T.Frustum().setFromProjectionMatrix(new T.Matrix4().multiplyMatrices(
                        camera.projectionMatrix, camera.matrixWorldInverse)),
                };
            }
            ({ clusters, frustum } = visibility.sky);
        }
        return group.some(prop => this.inPvs(prop.clusters, clusters) && (!frustum ||
            (prop.loadBounds ? frustum.intersectsBox(prop.loadBounds) : frustum.containsPoint(new T.Vector3(...prop.origin)))));
    }
    async world(data, rpc, active, progress = () => {}) {
        this.mapEntities = data.entities;
        const started = performance.now();
        log.info("build world", { faces: data.geometry.length, models: data.models.length });
        this.visibility = new Visibility(data.visibility);
        this.teleports = new ViewerTeleports(data.models, this.visibility);
        this.debugRendering = new DebugRendering(this, data);
        this.debugRendering.set(this.debugOptions ?? {});
        this.lighting.setup(data, this);
        this.reset(data.entities, data.models);
        const skyCamera = data.entities.find(
            (e) => e.classname === "sky_camera",
        );
        if (skyCamera) {
            this.skyDefinition = {
                origin: new T.Vector3(...numbers(skyCamera.origin)),
                scale: Math.max(1, Number(skyCamera.scale) || 16),
            };
            // Source's 3D sky pass restricts world rendering to sky_camera's
            // BSP area. PVS overlap is visibility, not ownership: using it to
            // move geometry between scenes puts ordinary world faces in the sky.
            this.skyArea = this.visibility.areaAt(this.skyDefinition.origin.toArray());
            if (this.skyArea < 0) this.diagnostic("Sky camera BSP area unavailable; keeping map geometry in the world scene.");
            this.skyFaces = this.visibility.facesInArea(this.skyArea);
            this.visibility.last = null;
        }
        const lightPages = [],
            pageSize = 1024;
        let page,
            x = 1,
            y = 1,
            row = 0;
        let prepared = 0;
        // Keep a texture's faces together in the atlas. BSP face order scatters
        // the same material across many pages, multiplying material variants
        // and draws in every color, water and shadow pass. Tall shelves first
        // also avoid wasting a row on a later, taller face. Geometry/PVS order
        // remains unchanged below.
        const lightmapFaces = [...data.geometry].sort((a, b) =>
            a.texture.localeCompare(b.texture) || b.lmH - a.lmH || b.lmW - a.lmW);
        for (const face of lightmapFaces) {
            if (prepared++ % 256 === 0) {
                progress(
                    `Building lightmaps · ${prepared} / ${data.geometry.length} faces`,
                );
                await new Promise((resolve) => setTimeout(resolve, 0));
                if (!active()) return;
            }
            if (
                face.lighting &&
                face.lmW < pageSize - 2 &&
                face.lmH < pageSize - 2
            ) {
                if (
                    !page ||
                    y + face.lmH + 1 >= pageSize ||
                    (x + face.lmW + 1 >= pageSize &&
                        y + row + face.lmH + 3 >= pageSize)
                ) {
                    page = new Float32Array(pageSize * pageSize * 4);
                    lightPages.push(page);
                    x = 1;
                    y = 1;
                    row = 0;
                }
                if (x + face.lmW + 1 >= pageSize) {
                    x = 1;
                    y += row + 2;
                    row = 0;
                }
                for (let j = -1; j <= face.lmH; j++)
                    for (let i = -1; i <= face.lmW; i++) {
                        const source =
                            (Math.min(face.lmH - 1, Math.max(0, j)) * face.lmW +
                                Math.min(face.lmW - 1, Math.max(0, i))) *
                            4;
                        page.set(
                            face.lighting.subarray(source, source + 4),
                            ((y + j) * pageSize + x + i) * 4,
                        );
                    }
                for (let i = 0; i < face.uv2.length; i += 2) {
                    face.uv2[i] = (x + face.uv2[i] * face.lmW) / pageSize;
                    face.uv2[i + 1] =
                        (y + face.uv2[i + 1] * face.lmH) / pageSize;
                }
                face.page = lightPages.length - 1;
                x += face.lmW + 2;
                row = Math.max(row, face.lmH);
                face.lighting = null;
            } else face.page = -1;
        }
        const maps = lightPages.map((bytes) => {
            const tex = this.track(
                new T.DataTexture(
                    bytes,
                    pageSize,
                    pageSize,
                    T.RGBAFormat,
                    T.FloatType,
                ),
            );
            tex.channel = 1;
            tex.magFilter = T.LinearFilter;
            tex.minFilter = T.LinearFilter;
            tex.needsUpdate = true;
            return tex;
        });
        log.info("lightmaps built", { pages: maps.length, pageSize,
            colorSpace: "linear", surfaceMultiplier: 1 });
        const batches = new Map();
        prepared = 0;
        for (const face of data.geometry) {
            if (prepared++ % 256 === 0) {
                progress(
                    `Building scene · ${prepared} / ${data.geometry.length} faces`,
                );
                await new Promise((resolve) => setTimeout(resolve, 0));
                if (!active()) return;
            }
            const geometry = this.geometry(face),
                center = geometry.boundingSphere.center;
            geometry.computeBoundingBox();
            const clusters = face.model === 0 && !face.displacement ? this.visibility.clustersForFace(face.face) :
                this.visibility.clustersForBounds(geometry.boundingBox.min.toArray(), geometry.boundingBox.max.toArray());
            const entity = face.model > 0 ? data.entities.find(e => e.model === `*${face.model}`) : null;
            if (entity && face.model > 0) {
                const transform = new T.Object3D(); transform.position.fromArray(numbers(entity.origin));
                this.angles(transform, numbers(entity.angles)); transform.updateMatrix();
                const bounds = geometry.boundingBox.clone().applyMatrix4(transform.matrix);
                clusters.splice(0, clusters.length, ...this.visibility.clustersForBounds(bounds.min.toArray(), bounds.max.toArray()));
            }
            const inSky = !!this.skyDefinition && this.skyArea >= 0 && (face.model > 0
                ? !!entity && this.visibility.areaAt(numbers(entity.origin)) === this.skyArea
                : face.displacement ? this.visibility.areaAt(center.toArray()) === this.skyArea
                : this.skyFaces.has(face.face));
            // VBSP marks water SURF_WARP, but compileSlime only sets
            // SURF_NODECALS. Keep these surfaces on separate planes too.
            const plane = (face.water || (face.surfaceFlags & 0x2000)) ? [...geometry.attributes.normal.array.slice(0, 3),
                new T.Vector3().fromBufferAttribute(geometry.attributes.normal, 0).dot(
                    new T.Vector3().fromBufferAttribute(geometry.attributes.position, 0))].map(v => v.toFixed(2)).join(",") : "";
            const key = `${inSky}:${face.texture}:${face.page}:${face.model}:${face.displacement}:${plane}:${Math.floor(center.x / 1024)},${Math.floor(center.y / 1024)},${Math.floor(center.z / 1024)}`;
            if (!batches.has(key))
                batches.set(key, { face, geometries: [], faces: [], inSky, clusters: new Set(), unclustered: false });
            const batch = batches.get(key);
            // A spatial batch is visible from any member cluster. An unknown
            // member must fail open, just as it did before batching. Cells bound
            // the extra geometry admitted by this conservative PVS union.
            if (!clusters.length) batch.unclustered = true;
            for (const cluster of clusters) batch.clusters.add(cluster);
            batch.geometries.push(geometry);
            batch.faces.push(face.face);
        }
        log.info("geometry batched", { batches: batches.size });
        let loadingInitial = true, completed = 0,
            built = 0;
        for (const batch of batches.values()) {
            if (built++ % 64 === 0) {
                progress(
                    `Building render batches · ${built} / ${batches.size}`,
                );
                await new Promise((resolve) => setTimeout(resolve, 0));
                if (!active()) return;
            }
            const geometry = this.track(mergeGeometries(batch.geometries));
            geometry.computeBoundingSphere();
            for (const g of batch.geometries) {
                g.dispose();
                this.resources.delete(g);
            }
            const placeholder = this.track(
                    new T.MeshBasicMaterial({ color: 0x777777 }),
                ),
                mesh = new T.Mesh(geometry, placeholder);
            const entity = data.entities.find(
                (e) => e.model === `*${batch.face.model}`,
            );
            mesh.castShadow = true; mesh.receiveShadow = true;
            if (entity && batch.face.model) {
                mesh.position.fromArray(numbers(entity.origin));
                this.angles(mesh, numbers(entity.angles));
            }
            mesh.updateMatrix(); mesh.matrixAutoUpdate = false;
            const inSky = batch.inSky;
            (inSky ? this.skyScene : this.scene).add(mesh);
            mesh.updateMatrixWorld(true); mesh.matrixWorldAutoUpdate = false;
            this.lighting.needsShadowUpdate = true;
            const renderItem = { mesh, faces: batch.faces, clusters: batch.unclustered ? [] : [...batch.clusters], model: batch.face.model,
                displacement: batch.face.displacement, inSky,
                mapHidden: entity?.startdisabled === "1",
                entityOrdinal: entity ? data.entities.indexOf(entity) : -1,
                hammerId: entity?.hammerid ?? null };
            if (!inSky) this.meshes.push(renderItem);
            if (batch.face.model > 0) this.brushPrototypes.push(renderItem);
            // Prepare every brush prototype before map readiness, including
            // those outside the initial view and those moved by live entities.
            const task = this.assetTask(async () => {
                try {
                    const source = await this.material(batch.face.texture, rpc, maps[batch.face.page]);
                    if (!active()) return;
                    const material = entity && batch.face.model ? this.track(source.clone()) : source;
                    if (material !== source) {
                        material.onBeforeCompile = source.onBeforeCompile;
                        material.customProgramCacheKey = source.customProgramCacheKey.bind(source);
                        applyRenderState(material, { renderMode: Number(entity.rendermode) || 0,
                            color: numbers(entity.rendercolor ?? "255 255 255"), alpha: Number(entity.renderamt ?? 255) });
                    }
                    mesh.material = material;
                    this.lighting.needsShadowUpdate = true;
                    if (material.userData.water) this.water.add(mesh);
                } catch (error) {
                    if (active() && error.name !== "AbortError") this.diagnostic(`${batch.face.texture}: ${error.message}`);
                } finally { if (active() && loadingInitial) progress(`Loading materials · ${++completed} / ${batches.size} batches`); }
            }, active);
            renderItem.assetTask = task;
        }
        await this.loadMapAssets();
        loadingInitial = false;
        if (!active()) return;
        this.worldBatches = new WorldBatches(this.meshes);
        progress("Loading sky textures…");
        const sky = data.entities.find(
            (e) => e.classname === "worldspawn",
        )?.skyname;
        if (sky) {
            try {
                const materials = await Promise.all(
                    skyFaces.map(({ suffix }) =>
                        this.material(`materials/skybox/${sky}${suffix}.vmt`, rpc),
                    ),
                );
                if (active()) {
                    const box = new T.Mesh(
                        this.track(skyGeometry()),
                        materials,
                    );
                    box.renderOrder = -10;
                    this.skyScene.add(box);
                    this.sky = box;
                    for (const material of materials) {
                        material.depthWrite = false;
                        material.fog = false;
                        if (material.map) {
                            material.map.wrapS = material.map.wrapT = T.ClampToEdgeWrapping;
                            material.map.needsUpdate = true;
                        }
                    }
                    log.info("sky ready", { sky, faces: skyFaces.map(({ suffix }) => suffix),
                        axes: "Source Z-up", imageOrigin: "top-left" });
                }
            } catch (e) {
                log.error("sky textures failed", e, { sky });
                this.diagnostic(`Sky: ${e.message}`);
            }
        }
        await this.sunSprites(data.entities, rpc, active);
        await this.projectedTextures(rpc, active);
        log.info("world ready", { batches: batches.size, meshes: this.meshes.length,
            resources: this.resources.size, durationMs: performance.now() - started });
    }
    async projectedTextures(rpc, active) {
        for (const light of this.lighting.dynamic.filter(light => light.projected && light.entity.texturename)) {
            const path = light.entity.texturename;
            try {
                const texture = await this.texture(texturePath(path), rpc);
                if (!active()) return;
                light.object.map = texture; this.lighting.needsShadowUpdate = true;
                log.info("projected light texture ready", { path });
            } catch (error) {
                if (!active() || error.name === "AbortError") return;
                log.warn("projected light texture unavailable", { path, message: error.message });
            }
        }
        this.lighting.refreshMasks();
    }
    async sunSprites(entities, rpc, active) {
        for (const entity of entities.filter(e => e.classname === "env_sun")) {
            const target = entity.target && entities.find(e => e.targetname === entity.target);
            const direction = entity.use_angles === "1" ? lightDirection(entity, [], true).negate() : target
                ? new T.Vector3(...numbers(entity.origin)).sub(new T.Vector3(...numbers(target.origin))).normalize()
                : new T.Vector3(0, 0, 1);
            const core = numbers(entity.rendercolor ?? "255 255 255"), max = Math.max(...core) || 255,
                color = new T.Color().setRGB(...core.map(v => v / max), T.SRGBColorSpace),
                overlay = numbers(entity.overlaycolor), overlayColor = overlay.some(v => v) ? new T.Color().setRGB(...overlay.map(v => v / 255), T.SRGBColorSpace) : color;
            for (const glow of [false, true]) {
                const path = (glow ? entity.overlaymaterial : entity.material) || "sprites/light_glow02_add_noz";
                let texture;
                try {
                    const { material } = await rpc("material", { path });
                    texture = await this.texture(texturePath(material.values.$basetexture), rpc);
                } catch (error) {
                    if (!active() || error.name === "AbortError") return;
                    log.warn("sun material unavailable; using procedural glow", { path, message: error.message });
                    const pixels = new Uint8Array(64 * 64 * 4);
                    for (let y = 0; y < 64; y++) for (let x = 0; x < 64; x++) {
                        const alpha = Math.max(0, 1 - Math.hypot(x - 31.5, y - 31.5) / 31.5) ** 2;
                        pixels.set([255, 255, 255, Math.round(alpha * 255)], (y * 64 + x) * 4);
                    }
                    texture = this.track(new T.DataTexture(pixels, 64, 64)); texture.needsUpdate = true;
                }
                if (!active()) return;
                const material = this.track(new T.SpriteMaterial({ map: texture, color: glow ? overlayColor : color,
                    blending: T.AdditiveBlending, depthWrite: false, depthTest: false, fog: false }));
                const sprite = new T.Sprite(material);
                sprite.renderOrder = glow ? -8 : -9; this.skyScene.add(sprite);
                this.lighting.suns.push({ sprite, direction, glow, size: Number(glow && Number(entity.overlaysize) >= 0 ? entity.overlaysize : entity.size) || 16 });
            }
            log.info("sun sprites ready", { direction: direction.toArray(), size: entity.size, useAngles: entity.use_angles });
        }
    }
    angles(object, angles) {
        object.rotation.order = "ZYX";
        object.rotation.set(
            (angles[2] * Math.PI) / 180,
            (angles[0] * Math.PI) / 180,
            (angles[1] * Math.PI) / 180,
        );
    }
    startMapParticles(rpc) {
        this.mapParticles?.dispose();
        const particles = this.mapParticles = new LiveParticles(this, rpc), desired = new Map();
        let index = 0;
        for (const entity of this.mapEntities ?? []) if (entity.classname === "info_particle_system" && Number(entity.start_active) && entity.effect_name) {
            const object = new T.Object3D(); object.position.fromArray(numbers(entity.origin)); this.angles(object, numbers(entity.angles)); object.updateMatrixWorld(true);
            const key = `map:${index++}`, points = [];
            for (let id = 1; id <= 63; id++) {
                const target = entity[`cpoint${id}`] && this.mapEntities.find(e => e.targetname === entity[`cpoint${id}`]);
                if (target) points.push({ id, parent: Number(entity[`cpoint${id}_parent`]) || 0, position: numbers(target.origin) });
            }
            desired.set(key, { name: entity.effect_name, source: key, flags: 1, startTime: 0, attachType: 2, offset: [0, 0, 0], controlPoints: points, object });
        }
        particles.entities = new Map([...desired].map(([key, spec]) => [key, { object: spec.object, state: { components: {} } }]));
        particles.reconcileDesired(desired); this.mapParticleStart = performance.now();
    }
    async props(props, rpc, active, progress = () => {}) {
        const started = performance.now();
        log.info("build props", { count: props.length });
        const groups = new Map();
        for (const p of props) {
            let inSky = false;
            if (this.skyFaces) {
                inSky = this.skyArea >= 0 && this.visibility.areaAt(p.origin) === this.skyArea;
            }
            p.inSky = inSky;
            const originLeaf = !p.leaves?.length ? this.visibility.leafAt?.(p.origin) : -1;
            const leaves = p.leaves?.length ? p.leaves : originLeaf >= 0 ? [originLeaf] : [];
            p.clusters = leaves.length ? this.visibility.clustersForLeaves(leaves) : [];
            // Until a model is decoded, its compiled leaf bounds conservatively
            // cover the prop for asset admission, including large props whose
            // origin alone is outside the viewing frustum.
            const bounds = this.visibility.boundsForLeaves?.(leaves);
            p.loadBounds = bounds ? new T.Box3(new T.Vector3(...bounds.mins), new T.Vector3(...bounds.maxs)) : null;
            const cell = p.origin.map(value => Math.floor(value / 1024)).join(",");
            // Adjacent props often straddle different BSP leaves. Keep spatial
            // cells bounded, but union their PVS instead of making a separate
            // instanced draw and baked-light texture for every leaf combination.
            const key = `${p.path}:${p.skin}:${inSky}:${!!p.static}:${p.flags & 0x10}:${cell}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(p);
        }
        const modelGroups = new Map();
        for (const group of groups.values()) {
            const key = `${group[0].path}:${group[0].skin}`;
            if (!modelGroups.has(key)) modelGroups.set(key, []);
            modelGroups.get(key).push(group);
        }
        const queue = [...modelGroups.values()];
        const concurrency = Math.min(6, queue.length);
        let completed = 0, loadingInitial = true;
        log.info("prop queue", { groups: groups.size, models: queue.length, concurrency });
        progress(`Loading props · 0 / ${queue.length} models`);
        const loadModel = async (modelGroups) => {
            const first = modelGroups[0][0];
            // World, sky and leaf groups share decoding, materials and geometry.
            const { meshes } = await rpc("model", { path: first.path, skin: first.skin });
            if (!active()) return;
            // Meshes can request independent materials together, while each
            // material's directory candidates retain their Source search order.
            const materials = await Promise.all(meshes.map(async (data) => {
                for (const path of data.materials) {
                    if (!active()) return null;
                    try {
                        return await this.material(path, rpc);
                    } catch (error) {
                        log.debug("model material search miss", { path, message: error.message });
                    }
                }
                return null;
            }));
            if (!active()) return;
            const maxVertices = Math.max(1, ...meshes.map(data => data.positions.length / 3));
            const perBatch = this.lighting ? Math.max(1, Math.floor(Math.min(4000000,
                this.renderer.capabilities.maxTextureSize * 1024) / maxVertices)) : Infinity;
            const geometries = meshes.map(data => this.geometry(data));
            const loadGroup = async group => {
                let yielded = performance.now();
                const first = group[0];
                for (let start = 0; start < group.length; start += perBatch) {
                    if (performance.now() - yielded > 8) {
                        await new Promise(resolve => setTimeout(resolve, 0));
                        if (!active()) return;
                        yielded = performance.now();
                    }
                    const instances = group.slice(start, start + perBatch);
                    meshes.forEach((data, index) => {
                        let material = materials[index] ?? this.track(
                            new T.MeshLambertMaterial({ color: 0xff00ff }),
                        );
                        const geometry = geometries[index];
                        if (this.lighting && material.userData.source && !material.userData.source.unlit) {
                            const baked = this.lighting.propTexture(data, instances, geometry, this, index), original = material;
                            material = this.track(original.clone());
                            material.onBeforeCompile = original.sourceShaderHook;
                            material.customProgramCacheKey = () => original.sourceShaderKey;
                            lightingMaterial(material, this.lighting, { prop: true, baked, ...original.sourceEmission });
                        }
                        const mesh = this.track(new T.InstancedMesh(geometry, material, instances.length)),
                            transform = new T.Object3D();
                        instances.forEach((p, i) => {
                            transform.position.fromArray(p.origin);
                            this.angles(transform, p.angles);
                            transform.scale.setScalar(p.scale);
                            transform.updateMatrix();
                            mesh.setMatrixAt(i, transform.matrix);
                            mesh.setColorAt(i, new T.Color(1, 1, 1));
                        });
                        mesh.userData.liveDynamicProp = !first.static;
                        mesh.computeBoundingSphere();
                        mesh.castShadow = instances.some(prop => !(prop.flags & 0x10)); mesh.receiveShadow = true;
                        if (this.lighting) this.lighting.needsShadowUpdate = true;
                        (first.inSky ? this.skyScene : this.scene).add(mesh);
                        (this.propMeshes ??= []).push(mesh);
                        mesh.updateMatrixWorld(true);
                        mesh.matrixAutoUpdate = mesh.matrixWorldAutoUpdate = false;
                        if (!first.inSky) (this.meshes ??= []).push({ mesh,
                            clusters: instances.some(prop => !prop.clusters.length) ? [] : [...new Set(instances.flatMap(prop => prop.clusters))], prop: true });
                    });
                }
            };
            // Transparent groups retain their original leaf partitions and
            // object sorting granularity; only opaque props combine leaves.
            const renderGroups = materials.some(material => material?.transparent) ? modelGroups.flatMap(group => {
                const leaves = new Map();
                for (const prop of group) {
                    const key = prop.clusters.join(',');
                    if (!leaves.has(key)) leaves.set(key, []);
                    leaves.get(key).push(prop);
                }
                return [...leaves.values()];
            }) : modelGroups;
            for (const group of renderGroups) {
                for (const prop of group) {
                    const transform = new T.Object3D();
                    transform.position.fromArray(prop.origin); this.angles(transform, prop.angles);
                    transform.scale.setScalar(prop.scale); transform.updateMatrix();
                    const bounds = new T.Box3();
                    for (const geometry of geometries) {
                        if (!geometry.boundingBox) geometry.computeBoundingBox();
                        bounds.union(geometry.boundingBox);
                    }
                    prop.loadBounds = bounds.applyMatrix4(transform.matrix);
                    if (!prop.static || !prop.leaves?.length) {
                        const clusters = this.visibility.clustersForBounds?.(prop.loadBounds.min.toArray(), prop.loadBounds.max.toArray());
                        if (clusters) prop.clusters = clusters;
                    }
                }
                this.assetTask(() => loadGroup(group), active, visibility => this.propGroupEligible(group, visibility));
            }
        };
        for (const modelGroups of queue) this.assetTask(async () => {
            try { await loadModel(modelGroups); }
            catch (error) { if (active() && error.name !== "AbortError") this.diagnostic(`${modelGroups[0][0].path}: ${error.message}`); }
            if (active()) {
                completed++;
                if (loadingInitial) progress(`Loading props · ${completed} / ${queue.length} models`);
            }
        }, active, visibility => modelGroups.some(group => this.propGroupEligible(group, visibility)));
        await this.loadVisibleAssets();
        loadingInitial = false;
        if (!active()) return;
        log.info("props ready", { groups: groups.size, models: completed, totalModels: queue.length,
            concurrency, resources: this.resources.size, durationMs: performance.now() - started });
    }
    staticPreviewUnchanged() {
        // A loaded, non-animated explorer needs no new GPU submission until an
        // input changes. Keep full cadence for live/asset previews, diagnostics,
        // loading, particles, water and lighting that can animate independently.
        if (!this.visibility || this.liveScene || this.previewClock || this.loading || this.assetsLoading ||
            this.performanceOverlay?.enabled || this.animated?.length || this.water?.surfaces.length ||
            this.lighting?.dynamic.length || this.mapParticles?.effects.size || this.mapParticles?.draining.size) {
            this.staticFrame = null; return false;
        }
        this.camera.updateMatrixWorld();
        const previous = this.staticFrame;
        const unchanged = previous && previous.epoch === this.epoch && previous.meshes === this.meshes.length &&
            previous.width === this.canvas.width && previous.height === this.canvas.height &&
            previous.matrix.equals(this.camera.matrixWorld) && previous.projection.equals(this.camera.projectionMatrix);
        if (!unchanged) this.staticFrame = { epoch: this.epoch, meshes: this.meshes.length,
            width: this.canvas.width, height: this.canvas.height,
            matrix: this.camera.matrixWorld.clone(), projection: this.camera.projectionMatrix.clone() };
        return !!unchanged;
    }
    frame(now = performance.now()) {
        this.performanceOverlay?.tickFps(now);
        const perf = this.performanceOverlay?.enabled ? this.performanceOverlay : null;
        const frameStart = perf ? performance.now() : 0;
        const details = perf?.details;
        let measured = details ? frameStart : 0;
        const dt = Math.min(0.05, (now - this.previous) / 1000);
        this.previous = now;
        const width = this.canvas.clientWidth,
            height = this.canvas.clientHeight;
        if (
            this.canvas.width !==
                Math.floor(width * this.renderer.getPixelRatio()) ||
            this.canvas.height !==
                Math.floor(height * this.renderer.getPixelRatio())
        ) {
            this.renderer.setSize(width, height, false);
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        }
        const forward = new T.Vector3(
            Math.cos(this.pitch) * Math.cos(this.yaw),
            Math.cos(this.pitch) * Math.sin(this.yaw),
            Math.sin(this.pitch),
        );
        const right = new T.Vector3(Math.sin(this.yaw), -Math.cos(this.yaw), 0),
            move = new T.Vector3();
        if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) move.add(forward);
        if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) move.sub(forward);
        if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) move.add(right);
        if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) move.sub(right);
        if (this.keys.has("Space")) move.z++;
        if (this.keys.has("KeyC"))
            move.z--;
        const previousPosition = this.camera.position.clone();
        this.camera.position.addScaledVector(
            move.normalize(),
            dt *
                this.speed *
                (this.keys.has("ShiftLeft") || this.keys.has("ShiftRight")
                    ? 4
                    : 1),
        );
        if (this.controls?.update) this.controls.update(dt);
        else this.camera.lookAt(this.camera.position.clone().add(forward));
        if (details) details.record('camera', performance.now() - measured);
        const presentationTime = this.previewClock ? this.previewClock(now, dt) : now;
        if (details) measured = performance.now();
        this.liveScene?.frame(presentationTime);
        if (details && this.liveScene) details.record('liveFrame', performance.now() - measured);
        if (this.teleports?.frame(this, previousPosition, this.camera.position, this.liveScene))
            this.camera.lookAt(this.camera.position.clone().add(new T.Vector3(Math.cos(this.pitch)*Math.cos(this.yaw), Math.cos(this.pitch)*Math.sin(this.yaw), Math.sin(this.pitch))));
        if (details) measured = performance.now();
        this.onLiveFrame?.(this.liveScene);
        if (details && this.onLiveFrame) details.record('liveCallbacks', performance.now() - measured);
        this.debugRendering?.update();
        if (this.staticPreviewUnchanged()) {
            this.animation = requestAnimationFrame(this.frame);
            return;
        }
        if (details) measured = performance.now();
        if (this.mapParticles?.enabled) this.mapParticles.update(now - this.mapParticleStart);
        else this.mapParticles?.suspend(now - this.mapParticleStart);
        if (details && this.mapParticles) details.record('particles', this.mapParticles.enabled ? performance.now() - measured : 0);
        if (details) measured = performance.now();
        this.lighting?.update(now / 1000);
        if (details) { details.record('styles', performance.now() - measured); measured = performance.now(); }
        const visibility = this.viewVisibility();
        this.pumpAssets(visibility);
        for (const item of this.meshes) {
            if (this.debugOptions?.[item.prop ? "renderProps" : "renderWorld"] === false) { item.mesh.visible = false; continue; }
            if (item.pvs !== visibility.clusters || item.inPvs === undefined) {
                item.pvs = visibility.clusters;
                item.inPvs = this.inPvs(item.clusters, visibility.clusters);
            }
            const inPvs = item.inPvs;
            // Retain off-PVS shadow casters on a layer absent from the viewing
            // camera; projected shadow cameras still see both layers.
            item.mesh.layers.set(inPvs ? 0 : 1);
            item.mesh.visible = this.debugOptions?.[item.prop ? "renderProps" : "renderWorld"] !== false && !item.liveHidden && !item.mapHidden && !(item.mesh.userData.liveDynamicProp && this.liveScene?.owned) &&
                (!item.assetTask || item.assetTask.started && !item.assetTask.running);
        }
        if (details) { details.record('visibility', performance.now() - measured); measured = performance.now(); }
        this.liveScene?.applyVisibility?.(visibility.clusters);
        if (this.liveScene) this.liveScene.root.visible = this.liveScene.skyRoot.visible = this.debugOptions?.renderLiveEntities !== false;
        this.skyScene.visible = this.debugOptions?.renderSky !== false;
        if (details && this.liveScene) details.record('liveVisibility', performance.now() - measured);
        if (this.skyScene.visible) {
            this.skyCamera.copy(this.camera);
            if (this.skyDefinition)
                this.skyCamera.position
                    .copy(this.camera.position)
                    .divideScalar(this.skyDefinition.scale)
                    .add(this.skyDefinition.origin);
            if (this.sky) this.sky.position.copy(this.skyCamera.position);
            for (const sun of this.lighting?.suns ?? []) {
                sun.sprite.position.copy(this.skyCamera.position).addScaledVector(sun.direction, 30000);
                sun.sprite.scale.setScalar(sun.size * 15);
                sun.sprite.material.opacity = sun.glow ? Math.max(0, this.camera.getWorldDirection(new T.Vector3()).dot(sun.direction)) : 1;
            }
        }
        if (this.lighting?.needsShadowUpdate && this.renderer.shadowMap) {
            this.renderer.shadowMap.needsUpdate = true;
            this.lighting.needsShadowUpdate = false;
        }
        if (this.debugOptions?.renderWorld !== false || this.debugOptions?.renderProps !== false) for (const texture of this.animated) {
            const elapsed = this.previewClock ? Math.max(0, presentationTime - (this.previousPresentationTime ?? presentationTime)) / 1000 : dt;
            texture.offset.x += elapsed * 0.02;
            texture.offset.y += elapsed * 0.01;
        }
        this.previousPresentationTime = presentationTime;
        // Loading changes the scene in bursts. A stationary preview needs only
        // occasional redraws; keep the GPU/main thread available for new assets.
        // Captured exploration and completed maps retain normal frame cadence.
        if (this.loading && document.pointerLockElement !== this.canvas &&
            now - (this.lastRender ?? -Infinity) < 100) {
            this.animation = requestAnimationFrame(this.frame);
            return;
        }
        this.lastRender = now;
        this.renderer.info.reset();
        // Water can submit this scene three extra times. Its world transforms
        // are fixed for this frame; update both hierarchies once for all passes.
        if (details) measured = performance.now();
        this.scene.updateMatrixWorld();
        this.skyScene.updateMatrixWorld();
        this.liveScene?.loadingSpinners?.update(now / 1000, this.camera);
        if (details) details.record('transforms', performance.now() - measured);
        const updateEnd = perf ? performance.now() : 0;
        let waterEnd = 0;
        perf?.beginGpu(now);
        const matrixWorldAutoUpdate = this.scene.matrixWorldAutoUpdate;
        const skyMatrixWorldAutoUpdate = this.skyScene.matrixWorldAutoUpdate;
        this.scene.matrixWorldAutoUpdate = this.skyScene.matrixWorldAutoUpdate = false;
        try {
            const water = this.debugOptions?.renderWorld !== false ? this.water : null;
            if (water) {
                this.scene.fog = this.skyScene.fog = this.water.fog(this.camera, this.visibility);
                this.water.render(this, now);
            }
            waterEnd = perf ? performance.now() : 0;
            if (details) details.record('water', waterEnd - updateEnd);
            this.renderer.autoClear = false;
            const waterTarget = water?.outputTarget(this.renderer);
            if (waterTarget) this.renderer.setRenderTarget(waterTarget);
            this.renderer.clear();
            if (this.skyScene.visible && this.skyScene.children.length) {
                this.renderer.render(this.skyScene, this.skyCamera);
                this.renderer.clearDepth();
            }
            this.scene.background = null;
            this.renderer.render(this.scene, this.camera);
            if (waterTarget) this.water.composite(this.renderer, now);
        } finally {
            this.scene.matrixWorldAutoUpdate = matrixWorldAutoUpdate;
            this.skyScene.matrixWorldAutoUpdate = skyMatrixWorldAutoUpdate;
            perf?.endGpu();
        }
        if (details) details.record('draw', performance.now() - waterEnd);
        if (details) measured = performance.now();
        this.onLiveRendered?.(this.liveScene);
        if (details && this.onLiveRendered) details.record('liveCallbacks', performance.now() - measured);
        perf?.sample(now, frameStart, updateEnd, waterEnd, performance.now());
        if (now - (this.lastStatistics ?? 0) >= 10000) {
            this.lastStatistics = now;
            log.debug("render statistics", { drawCalls: this.renderer.info.render.calls,
                triangles: this.renderer.info.render.triangles, geometries: this.renderer.info.memory.geometries,
                textures: this.renderer.info.memory.textures });
        }
        this.animation = requestAnimationFrame(this.frame);
    }
    setDebug(options) {
        this.staticFrame = null;
        this.debugOptions = { ...this.debugOptions, ...options };
        this.debugRendering?.set(this.debugOptions);
    }
}
